import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// components
import AboutCard from 'eCoach/convoPrep/AboutCard';
import Chatbar from 'eCoach/chatbar/Chatbar';
import ChatBubble from 'eCoach/ChatBubble';
import ChatHeader from 'eCoach/ChatHeader';
import ChatStart from 'eCoach/chatStart/_index';
import ChatDatestamp from 'eCoach/ChatDatestamp';
import ConversationItemsCard from 'eCoach/convoPrep/ConversationItemsCard';
import ECoachConfirmationInput from 'eCoach/ECoachConfirmationInput';
import IntroMessage from 'eCoach/chatStart/IntroMessage';
import Loader from 'eCoach/loaders/Loader';
import PolicyHelper from 'eCoach/chatStart/PolicyHelper';
import RAGCard, { RAGOutput } from 'eCoach/RAGCard';
import RAGCardWithFormOption, {
  RAGOutputWithFormOption,
} from 'eCoach/RAGCardWithFormOption';
import SuggestionItemsCard from 'eCoach/suggestions/SuggestionItemsCard';
import UnionHelper from 'eCoach/chatStart/UnionHelper';
import WarningLetterDateInput from 'eCoach/warningLetter/WarningLetterDateInput';
import WarningLetterIntegerInput from 'eCoach/warningLetter/WarningLetterIntegerInput';
import WarningLetterReasonInput from 'eCoach/warningLetter/WarningLetterReasonInput';
import WarningLetterSingleDateInput from 'eCoach/warningLetter/WarningLetterSingleDateInput';
import WarningLetterTemplate from 'eCoach/warningLetter/WarningLetterTemplate';
import WarningLetterTypeInput from 'eCoach/warningLetter/WarningLetterTypeInput';
import FormFillingTemplateSelection from 'eCoach/formFilling/FormFillingTemplateSelection';

// context & hooks
import useAutoScroll from 'eCoach/hooks/useAutoScroll';
import { usePosthogCapture } from 'eCoach/posthogCapture/chat';
import useWebsocketSetup from 'shared/hooks/useWebsocketSetup';
import {
  useUpdateSessionStatus,
  ECoachSession as ECoachSessionType,
} from 'shared/hooks/api/ecoach';

// utils
import routesConfig from 'constants/routesConfig.json';
import { Step } from 'types/step';
import { OutputType, UserInputType } from '../utils';
import {
  isGetPartialMessage,
  isValidationError,
  MaestroWSMessage,
} from './maestroWSTypes';
import FormBuilder from '../formFilling/FormBuilder';

interface ChatGuideData {
  chatStartEnded: boolean;
  displayPromptHelper: boolean;
  inProgress: boolean;
  promptHelperGoal: string;
  promptHelperTheme: string;
  displayPolicyPrompt?: boolean;
  displayUnionPrompt?: boolean;
}

interface InputPending {
  message: string;
  inputType: number;
}

interface ECoachSessionProps {
  activeSession?: ECoachSessionType | null;
  loading?: boolean;
  chatGuideData: ChatGuideData;
  createSession: () => void;
  handleChatGuidance: (data: Partial<ChatGuideData>) => void;
  steps: Step[];
  setSteps: React.Dispatch<React.SetStateAction<Step[]>>;
  onBackClick?: () => void;
  showBackButton?: boolean;
}

export default function ECoachSession({
  activeSession,
  loading,
  chatGuideData,
  createSession,
  handleChatGuidance,
  steps,
  setSteps,
  onBackClick,
  showBackButton = false,
}: ECoachSessionProps): JSX.Element {
  const [inputPendingSend, setInputPendingSend] = useState<InputPending | null>(
    null
  );
  const { mutate: updateStatus } = useUpdateSessionStatus();
  const chat = usePosthogCapture();
  const messagesEndRef = useAutoScroll(steps);

  const captureSubmission = useCallback(
    (inputType: (typeof UserInputType)[keyof typeof UserInputType]) => {
      switch (inputType) {
        case UserInputType.INITIAL_PROMPT:
          chat.messageInitialPromptSubmitted();
          break;
        case UserInputType.BACK_AND_FORTH_CONVERSATION_INPUT:
          chat.messageBackAndForthSubmitted();
          break;
        case UserInputType.RESOLUTION_SUMMARY_INPUT:
          chat.messageResolutionSubmitted({ step: 'RESOLUTION_SUMMARY_INPUT' });
          break;
        case UserInputType.UNBLOCKER_ANSWER:
          chat.messageUnblocker({ step: 'UNBLOCKER_ANSWER' });
          break;
        case UserInputType.UNBLOCKER_FOLLOW_UP_ANSWER:
          chat.messageUnblocker({ step: 'UNBLOCKER_FOLLOW_UP_ANSWER' });
          break;
        case UserInputType.CONVERSATION_PREP_WITH_WHO:
          chat.messageInitialPromptConvoPrepSubmitted({
            step: 'CONVERSATION_PREP_WITH_WHO',
          });
          break;
        case UserInputType.CONVERSATION_PREP_GOAL:
          chat.messageInitialPromptConvoPrepSubmitted({
            step: 'CONVERSATION_PREP_GOAL',
          });
          break;
        case UserInputType.CONVERSATION_PREP_WORRIED_BY:
          chat.messageInitialPromptConvoPrepSubmitted({
            step: 'CONVERSATION_PREP_WORRIED_BY',
          });
          break;
        case UserInputType.CHALLENGE_VERIFICATION_ANSWER:
          chat.messageChallengeVerification({
            step: 'CHALLENGE_VERIFICATION_ANSWER',
          });
          break;
        case UserInputType.CHALLENGE_DESCRIPTION_FEEDBACK:
          chat.messageChallengeVerification({
            step: 'CHALLENGE_DESCRIPTION_FEEDBACK',
          });
          break;
        case UserInputType.RESOLUTION_TAKEN_ACTION_ANSWER:
          chat.messageResolutionSubmitted({
            step: 'RESOLUTION_TAKEN_ACTION_ANSWER',
          });
          break;
        case UserInputType.RESOLUTION_IS_RESOLVED_ANSWER:
          chat.messageResolutionSubmitted({
            step: 'RESOLUTION_IS_RESOLVED_ANSWER',
          });
          break;
        case UserInputType.RESOLUTION_CONFIDENCE_ANSWER:
          chat.messageResolutionSubmitted({
            step: 'RESOLUTION_CONFIDENCE_ANSWER',
          });
          break;
        case UserInputType.OUTCOME_VERIFICATION_ANSWER:
          chat.messageResolutionSubmitted({
            step: 'OUTCOME_VERIFICATION_ANSWER',
          });
          break;
        case UserInputType.CONVERSATION_PREP_START:
          chat.messageInitialPromptConvoPrepSubmitted({
            step: 'CONVERSATION_PREP_START',
          });
          break;
        default:
          chat.chatbarSubmittedMessage();
          break;
      }
    },
    [chat]
  );
  const { t } = useTranslation('pageEcoach');
  const { sendJsonMessage, lastJsonMessage } =
    useWebsocketSetup<MaestroWSMessage>(routesConfig.ECOACH.MAESTRO_WS);
  const [partialText, setPartialText] = useState('');
  const [awaitingResponse, setAwaitingResponse] = useState(false);

  const sendUserInput = useCallback(
    (
      message: string,
      inputType: (typeof UserInputType)[keyof typeof UserInputType] = UserInputType.BACK_AND_FORTH_CONVERSATION_INPUT,
      extraData: Record<string, string> | undefined = undefined
    ) => {
      captureSubmission(inputType);
      if (!activeSession) {
        createSession();
        setInputPendingSend({ inputType, message });
        setAwaitingResponse(true);
        return;
      }

      setInputPendingSend(null);
      setAwaitingResponse(true);
      sendJsonMessage({
        action: 'evaluate',
        request_id: Date.now(),
        pk: activeSession.id,
        data: { input_type: inputType, message, extra_data: extraData },
      });

      setSteps((prevSteps) => [
        ...prevSteps,
        {
          created_at: new Date().toISOString(),
          user_inputs: [{ message, extra_data: extraData }],
        },
      ]);
    },
    [activeSession, captureSubmission, createSession, sendJsonMessage, setSteps]
  );

  // #region Send first input once session has been created
  useEffect(() => {
    if (activeSession && inputPendingSend) {
      sendUserInput(
        inputPendingSend.message,
        inputPendingSend.inputType as any
      );
    }
  }, [activeSession, inputPendingSend, sendUserInput]);
  // #endregion Send first input once session has been created

  // #region Load session steps
  useEffect(() => {
    // Only override steps once session steps have been loaded
    if (
      activeSession?.steps &&
      Array.isArray(activeSession.steps) &&
      activeSession.steps.length > 0
    ) {
      setSteps(activeSession.steps);
    }

    // We don't want to update each time steps.length updates, so it's not in the dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSession, setSteps]);
  // #endregion Load session steps

  // #region Receive eCoach response
  useEffect(() => {
    if (lastJsonMessage === null) return;
    if (isValidationError(lastJsonMessage)) return;

    if (!lastJsonMessage.data) return;

    if (isGetPartialMessage(lastJsonMessage)) {
      setAwaitingResponse(false);
      setPartialText((prev) => prev + lastJsonMessage.data);
      return;
    }

    setAwaitingResponse(false);
    setPartialText('');
    setSteps((prevSteps) => {
      if (prevSteps.length === 0) {
        return [lastJsonMessage.data];
      }

      const previousSteps = [...prevSteps];
      const lastStep = previousSteps[previousSteps.length - 1];
      const lastStepHasOutputs = Array.isArray(lastStep.outputs)
        ? lastStep.outputs.length > 0
        : false;

      if (!lastStepHasOutputs) {
        previousSteps[previousSteps.length - 1] = lastJsonMessage.data;
        return previousSteps;
      }

      return [...previousSteps, lastJsonMessage.data];
    });
  }, [lastJsonMessage, setSteps, setPartialText]);
  // #endregion Receive eCoach response

  const messageCards = useMemo(() => {
    // Always show policy follow-up question when active, regardless of session state
    if (chatGuideData.displayPolicyPrompt) {
      const cards = [];
      cards.push(
        <ChatBubble
          key="user-policy-question"
          simpleString={t(
            'chatStart.I need information about my company policies'
          )}
          sentByUser
        />
      );
      cards.push(
        <ChatBubble
          key="policy-follow-up"
          simpleString={t('chatStart.PolicyFollowUp')}
        />
      );
      return cards;
    }
    if (!activeSession) return [];
    return steps?.map((step, i) => {
      const timestamps = [];
      timestamps.push(
        <ChatDatestamp
          index={i}
          timestamp={step.created_at}
          prevTimestamp={steps[i - 1]?.created_at}
        />
      );

      const userInputCards = [];
      if (step.user_inputs) {
        userInputCards.push(
          Object.values(step.user_inputs).map((userInput, index) => (
            <ChatBubble
              key={index}
              simpleString={userInput.message}
              sentByUser
            />
          ))
        );
      }

      const outputCards = [];
      if (step.outputs) {
        outputCards.push(
          Object.values(step.outputs).map((output, index) => {
            if (output.output_type === OutputType.CONVERSATION_PLAN_ABOUT)
              return <AboutCard key={index} />;

            if (output.output_type === OutputType.SUGGESTIONS)
              return (
                <SuggestionItemsCard
                  activeSession={activeSession.id}
                  stepOutput={output}
                  key={index}
                />
              );

            if (output.output_type === OutputType.RAG_SUMMARY)
              return output.rag ? (
                <RAGCard key={index} output={output as RAGOutput} />
              ) : null;

            // TODO: Include this in RAG component directly instead of duplicated version
            if (
              output.output_type === OutputType.RAG_SUMMARY &&
              output.rag &&
              output.rag.document_translations.some(
                (doc) => doc.has_form_template
              )
            )
              return output.rag ? (
                <RAGCardWithFormOption
                  key={index}
                  output={output as RAGOutputWithFormOption}
                  sendUserInput={sendUserInput}
                />
              ) : null;

            if (output.output_type === OutputType.CONVERSATION_PLAN)
              return (
                <ConversationItemsCard
                  activeSession={activeSession}
                  key={index}
                  stepOutput={output}
                />
              );

            if (output.output_type === OutputType.WRITTEN_WARNING_TEMPLATE)
              return <WarningLetterTemplate key={index} output={output} />;

            if (output.output_type === OutputType.FORM_INSTANCE) {
              return (
                <FormBuilder key={index} formInstance={output.form_instance} />
              );
            }

            return <ChatBubble key={index} simpleString={output.message} />;
          })
        );
      } else if (partialText) {
        outputCards.push(
          <ChatBubble key={`partial-bubble-${i}`} simpleString={partialText} />
        );
      } else if (!awaitingResponse) {
        outputCards.push(
          <ChatBubble key={`loading-bubble-${i}`}>
            {React.createElement(Loader, { type: 'typing' } as any)}
          </ChatBubble>
        );
      }

      return [...timestamps, ...userInputCards, ...outputCards];
    });
  }, [
    activeSession,
    steps,
    chatGuideData,
    t,
    partialText,
    awaitingResponse,
    sendUserInput,
  ]);

  const inputComponent = useMemo(() => {
    if (!steps) return <></>;

    const lastStep = steps[steps.length - 1];
    if (!lastStep?.outputs) {
      return <></>;
    }

    const lastOutput = lastStep.outputs[lastStep.outputs.length - 1];
    switch (lastOutput?.output_type) {
      case OutputType.CONVERSATION_PREP_WITH_WHO:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.CONVERSATION_PREP_WITH_WHO)
            }
          />
        );
      case OutputType.CONVERSATION_PREP_GOAL:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.CONVERSATION_PREP_GOAL)
            }
          />
        );
      case OutputType.CONVERSATION_PREP_WORRIED_BY:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.CONVERSATION_PREP_WORRIED_BY)
            }
          />
        );
      case OutputType.CHALLENGE_VERIFICATION_CONFIRMATION_QUESTION:
        return (
          <ECoachConfirmationInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.CHALLENGE_VERIFICATION_CONFIRMATION_ANSWER}
          />
        );
      case OutputType.RESOLUTION_TAKEN_ACTION_QUESTION:
        return (
          <ECoachConfirmationInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.RESOLUTION_TAKEN_ACTION_ANSWER}
            alternativeOption={{
              action: () =>
                activeSession &&
                updateStatus({ id: activeSession.id, status: 'skipped' }),
              text: t('Skip'),
            }}
          />
        );
      case OutputType.RESOLUTION_IS_RESOLVED_QUESTION:
        return (
          <ECoachConfirmationInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.RESOLUTION_IS_RESOLVED_ANSWER}
          />
        );
      case OutputType.RESOLUTION_RESOLVED_TAKEN_ACTION_QUESTION:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.OUTCOME_VERIFICATION_ANSWER)
            }
          />
        );
      case OutputType.OUTCOME_CONFIDENCE_QUESTION:
        return (
          <ECoachConfirmationInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.RESOLUTION_CONFIDENCE_ANSWER}
          />
        );
      case OutputType.OUTCOME_VERIFICATION_QUESTION:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.OUTCOME_VERIFICATION_ANSWER)
            }
          />
        );
      case OutputType.UNBLOCKER_QUESTION:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.UNBLOCKER_ANSWER)
            }
          />
        );
      case OutputType.UNBLOCKER_FOLLOW_UP_QUESTION:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.UNBLOCKER_FOLLOW_UP_ANSWER)
            }
          />
        );
      case OutputType.CHALLENGE_VERIFICATION_QUESTION:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(
                message,
                UserInputType.CHALLENGE_VERIFICATION_ANSWER
              )
            }
          />
        );
      case OutputType.WARNING_LETTER_QUESTION:
        return (
          <Chatbar
            onSubmit={(message) =>
              sendUserInput(message, UserInputType.WARNING_LETTER_RESPONSE)
            }
          />
        );
      case OutputType.WARNING_LETTER_TYPE_QUESTION:
        return (
          <WarningLetterTypeInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.WARNING_LETTER_RESPONSE}
          />
        );
      case OutputType.WARNING_LETTER_DATE_QUESTION:
        return (
          <WarningLetterDateInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.WARNING_LETTER_RESPONSE}
            steps={steps}
          />
        );
      case OutputType.WARNING_LETTER_REASON_QUESTION:
        return (
          <WarningLetterReasonInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.WARNING_LETTER_RESPONSE}
            steps={steps}
          />
        );
      case OutputType.WARNING_LETTER_DATE_QUESTION_SINGLE:
        return (
          <WarningLetterSingleDateInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.WARNING_LETTER_RESPONSE}
          />
        );
      case OutputType.WARNING_LETTER_INTEGER:
        return (
          <WarningLetterIntegerInput
            sendUserInput={sendUserInput}
            inputType={UserInputType.WARNING_LETTER_RESPONSE}
          />
        );
      case OutputType.FORM_TEMPLATE_SELECTION:
        return (
          <FormFillingTemplateSelection
            sendUserInput={sendUserInput}
            inputType={UserInputType.FORM_TEMPLATE_CHOICE}
            message={lastOutput.message}
          />
        );
      case OutputType.RAG_SUMMARY:
        if (lastOutput.rag.has_fillable_forms) {
          return (
            <FormFillingTemplateSelection
              sendUserInput={sendUserInput}
              inputType={UserInputType.FORM_TEMPLATE_CHOICE}
              message={lastOutput.message}
              choices={lastOutput.rag.document_translations
                .filter((doc) => !!doc.form_template)
                .map((doc) => ({
                  id: doc.form_template,
                  displayName: doc.title,
                }))}
            />
          );
        }
        return <Chatbar onSubmit={sendUserInput} />;
      default:
        return <Chatbar onSubmit={sendUserInput} />;
    }
  }, [activeSession, sendUserInput, steps, updateStatus, t]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        height: '100%',
      }}
    >
      {steps && (
        <ChatHeader
          steps={steps}
          onBackClick={onBackClick}
          showBackButton={showBackButton}
        />
      )}
      <div
        style={{
          display: 'flex',
          height: '100%',
          flexDirection: 'column',
          gap: '30px',
          marginBottom: '50px',
        }}
      >
        <IntroMessage key="IntroMessage" />
        {steps.length > 0 && <ChatDatestamp timestamp={steps[0].created_at} />}
        {/* Pending user bubble removed intentionally to avoid flicker during session activation */}
        {activeSession && messageCards}
        {activeSession && awaitingResponse && !partialText && (
          <ChatBubble key="awaiting-loader">
            {React.createElement(Loader, { type: 'typing' } as any)}
          </ChatBubble>
        )}
        {chatGuideData.displayPolicyPrompt && (
          <PolicyHelper
            handleChatGuidance={handleChatGuidance}
            sendUserInput={sendUserInput}
          />
        )}
        {chatGuideData.displayUnionPrompt && (
          <UnionHelper
            handleChatGuidance={handleChatGuidance}
            sendUserInput={sendUserInput}
          />
        )}
      </div>
      {!activeSession &&
        !loading &&
        !inputPendingSend &&
        !chatGuideData.displayPolicyPrompt &&
        !chatGuideData.displayUnionPrompt && (
          <ChatStart
            key="start_chat"
            chatGuideData={chatGuideData}
            handleChatGuidance={handleChatGuidance}
            sendUserInput={sendUserInput}
          />
        )}
      {activeSession && inputComponent}
      <div ref={messagesEndRef} style={{ margin: 0 }} />
    </div>
  );
}
